import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaHeart,
  FaLeaf,
  FaCheckCircle,
  FaLock,
  FaTruck,
  FaStar,
} from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import { CiShoppingCart } from "react-icons/ci";
import { HiCheck } from "react-icons/hi";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../feature/cartSlice";
import { toggleWishlist } from "../../feature/wishlist";
import useWishlist from "../../customhooks/useWishlist";
import Banner2 from "../../assets/banner2.png";
import Banner3 from "../../assets/0340fa95-264e-4f12-83bc-65cc7c90b5c8.png";
import cat1 from "../../assets/clothes.png";
import cat2 from "../../assets/jewellry.png";
import cat3 from "../../assets/ayurveda.png";
import product1 from "../../assets/0ac21118-db2c-4f93-96f1-ebf6e99d0630.png";
import product2 from "../../assets/2e1c47d0-9bca-4801-a4ea-817142a34509.png";
import product3 from "../../assets/992246df-4534-4543-8620-8d1b29a4ceb5.png";
import product4 from "../../assets/Ashwagandha Botanical Supplement Showcase.png";

/* ---------------------------------------------------
   THEME + CONFIG
--------------------------------------------------- */

const GREEN = "#1f5a2e";
const GREEN_DARK = "#17451f";
const CREAM = "#f6f8f2";

// from .env  ->  REACT_APP_BASE_API=http://localhost:5000/api
const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");

// Local fallback images (used only when a product has no image / image fails)
const fallbackProductImages = [product1, product2, product3, product4];

const trustItems = [
  { icon: FaLeaf, label: "100% Natural" },
  { icon: FaCheckCircle, label: "Authentic Products" },
  { icon: FaLock, label: "Secure Payments" },
  { icon: FaTruck, label: "Fast Delivery" },
];

/* ---------------------------------------------------
   HELPERS
--------------------------------------------------- */

// API may return [] or { data: [] } or { products: [] } or { categories: [] }
const extractList = (json) => {
  if (Array.isArray(json)) return json;
  if (Array.isArray(json?.data)) return json.data;
  if (Array.isArray(json?.products)) return json.products;
  if (Array.isArray(json?.categories)) return json.categories;
  if (Array.isArray(json?.data?.products)) return json.data.products;
  if (Array.isArray(json?.data?.categories)) return json.data.categories;
  if (Array.isArray(json?.items)) return json.items;
  return [];
};

// Category table has no image, so pick a local image by name, then by position
const getCategoryImage = (name = "", index = 0) => {
  const n = String(name).toLowerCase();
  if (n.includes("ayurved") || n.includes("wellness")) return cat3;
  if (n.includes("cloth")) return cat1;
  if (n.includes("jewel")) return cat2;
  return [cat3, cat1, cat2][index % 3];
};

// "abc.png" -> BASE/uploads/abc.png, "uploads/abc.png" -> BASE/uploads/abc.png
const toImageUrl = (path, fallback) => {
  const src = typeof path === "string" ? path : path?.url;
  if (!src) return fallback;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return clean.includes("/")
    ? `${BASE_API}/${clean}`
    : `${BASE_API}/uploads/${clean}`;
};

const isTrue = (v) => v === true || v === 1 || v === "1" || v === "true";

// If your API field names are different, change them only here
const normalizeProduct = (p, index = 0) => {
  const fallback = fallbackProductImages[index % fallbackProductImages.length];
  const firstImage = Array.isArray(p.images) ? p.images[0] : null;
  const price = Number(p.price ?? p.sellingPrice ?? p.salePrice ?? 0);
  const oldRaw = p.mrp ?? p.originalPrice ?? p.oldPrice ?? p.old_price ?? null;

  return {
    id: p.id ?? p._id,
    name: p.name ?? p.title ?? "Untitled product",
    category:
      p.category_name ??
      p.categoryName ??
      p.category?.name ??
      (typeof p.category === "string" ? p.category : ""),
    price,
    oldPrice: oldRaw && Number(oldRaw) > price ? Number(oldRaw) : null,
    rating: Number(p.rating ?? p.averageRating ?? 0),
    sold: Number(p.sold_count ?? p.soldCount ?? p.sold ?? 0),
    isBestseller: isTrue(
      p.is_best_seller ??
        p.is_bestseller ??
        p.isBestseller ??
        p.isBestSeller ??
        p.bestseller,
    ),
    // used to sort "Recently Added" (change field names here if needed)
    createdAt:
      new Date(
        p.created_at ?? p.createdAt ?? p.created_on ?? p.date_added ?? 0,
      ).getTime() || 0,
    img: toImageUrl(p.image ?? p.img ?? p.thumbnail ?? firstImage, fallback),
    fallback,
  };
};

/* ---------------------------------------------------
   COMPONENT
--------------------------------------------------- */

export default function Home() {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  // const wishItems = useSelector((state) => state.wishlist.items);
  const { items: wishItems, toggle: toggleWish } = useWishlist();
  const [categories, setCategories] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState("");
  const [hero, setHero] = useState(null);
  const [heroLoading, setHeroLoading] = useState(true);

  /* ---------- fetch categories ---------- */
  useEffect(() => {
    const controller = new AbortController();

    const loadCategories = async () => {
      try {
        const res = await fetch(`${BASE_API}/category`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to load categories");
        const json = await res.json();

        const list = extractList(json).map((c, i) => ({
          id: c.id ?? c._id,
          title: c.name ?? c.title,
          to: `/product-category/${c.id ?? c._id}`,
          img: toImageUrl(c.image, null),
        }));
        setCategories(list);
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      } finally {
        setLoadingCats(false);
      }
    };

    loadCategories();
    return () => controller.abort();
  }, []);

  /* ---------- fetch best sellers + recent products (from /products) ---------- */
  useEffect(() => {
    const controller = new AbortController();

    const loadProducts = async () => {
      try {
        const res = await fetch(`${BASE_API}/products`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to load products");
        const json = await res.json();

        const all = extractList(json).map(normalizeProduct);

        // 1) products flagged as bestseller, 2) otherwise most sold,
        // 3) otherwise just the first products
        const flagged = all.filter((p) => p.isBestseller);
        const source = flagged.length
          ? flagged
          : [...all].sort((a, b) => b.sold - a.sold);

        setBestSellers(source.slice(0, 4));

        // newest first: by created date, falling back to higher id
        const recent = [...all].sort(
          (a, b) => b.createdAt - a.createdAt || Number(b.id) - Number(a.id),
        );
        setRecentProducts(recent.slice(0, 4));
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
    return () => controller.abort();
  }, []);

  /* ---------- fetch hero banner ---------- */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_API}/banners`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : []))
      .then((json) => setHero(extractList(json)[0] || null)) // lowest order number = hero
      .catch(() => {})
      .finally(() => setHeroLoading(false));
    return () => controller.abort();
  }, []);

  /* ---------- handlers ---------- */
  const handleAddToCart = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      addToCart({
        id: p.id,
        name: p.name,
        price: p.price,
        image: p.img,
        qty: 1,
      }),
    );
  };

  const handleWishlist = (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWish({
      id: p.id,
      name: p.name,
      price: p.price,
      image: p.img,
      category: p.category,
    });
  };

  /* ---------- shared product card (Best Sellers + Recently Added) ---------- */
  const renderProductCard = (p) => {
    const inCart = cartItems.some((i) => i.id === p.id);
    const inWishlist = wishItems.some((i) => i.id === p.id);

    return (
      <Link
        key={p.id}
        to={`/product/${p.id}`}
        className="group block bg-white border border-[#e4ead9] rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
      >
        <div className="relative w-full aspect-square bg-[#f3f6ee] overflow-hidden">
          <button
            type="button"
            onClick={(e) => handleWishlist(e, p)}
            aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
            className="absolute top-3 right-3 z-10 w-[34px] h-[34px] rounded-full bg-white shadow-md flex items-center justify-center"
            style={{ color: GREEN }}
          >
            {inWishlist ? (
              <FaHeart className="text-[15px]" />
            ) : (
              <FiHeart className="text-[15px]" />
            )}
          </button>
          <img
            src={p.img}
            alt={p.name}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = p.fallback;
            }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>

        <div className="p-3 sm:p-4">
          <h3 className="text-[14px] sm:text-[16px] text-[#111] font-[500] mb-1 leading-snug">
            {p.name}
          </h3>

          <div className="flex items-center gap-1 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <FaStar
                key={i}
                className={`text-[11px] ${
                  i < Math.round(p.rating) ? "text-[#e0a526]" : "text-[#e5e5e5]"
                }`}
              />
            ))}
            <span className="text-[11px] text-[#888] ml-1">{p.rating}</span>
          </div>

          <p className="text-[15px] font-[600] text-[#111] mb-3">
            ₹{p.price}
            {p.oldPrice && (
              <span className="line-through text-[#999] font-normal text-[12px] ml-2">
                ₹{p.oldPrice}
              </span>
            )}
          </p>

          <button
            type="button"
            onClick={(e) => handleAddToCart(e, p)}
            className="w-full flex items-center justify-center gap-2 text-white text-[12px] sm:text-[14px] font-[500] py-2 sm:py-[10px] rounded-md transition-colors duration-300"
            style={{ backgroundColor: inCart ? GREEN_DARK : GREEN }}
          >
            {inCart ? (
              <>
                <HiCheck className="text-[16px]" /> Added
              </>
            ) : (
              <>
                <CiShoppingCart className="text-[18px]" /> Add to Cart
              </>
            )}
          </button>
        </div>
      </Link>
    );
  };

  // shared loading skeleton for product grids
  const renderProductSkeletons = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="border border-[#e4ead9] rounded-lg overflow-hidden animate-pulse"
      >
        <div className="w-full aspect-square bg-[#f3f6ee]" />
        <div className="p-4 space-y-3">
          <div className="h-4 bg-[#eef2e6] rounded" />
          <div className="h-3 bg-[#eef2e6] rounded w-1/2" />
          <div className="h-8 bg-[#eef2e6] rounded" />
        </div>
      </div>
    ));

  const heroImg = hero?.image ? toImageUrl(hero.image, Banner2) : Banner2;
  const heroLink = hero?.button_link || "/shop";
  const heroBtnText = hero ? hero.button_text : "Shop Ayurveda";
  const heroSub = hero ? hero.subtitle : "Ancient wisdom. Modern living.";
  const heroBtn = (
    <button
      className="inline-flex items-center gap-2 text-white text-[13px] sm:text-[15px] font-[500] px-5 py-3 rounded-full transition-colors duration-300"
      style={{ backgroundColor: GREEN }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = GREEN_DARK)}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = GREEN)}
    >
      {heroBtnText}
      <FaArrowRight className="text-[12px]" />
    </button>
  );

  return (
    <div className="w-full overflow-hidden bg-white">
      {/* ================= HERO ================= */}
      <section
        className="relative w-full mt-[60px] min-h-[380px] sm:min-h-[520px] bg-cover bg-center flex items-center"
        style={
          heroLoading
            ? { backgroundColor: CREAM }
            : { backgroundImage: `url(${heroImg})` }
        }
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#f6f8f2] via-[#f6f8f2]/85 to-transparent" />

        {!heroLoading && (
          <div className="relative z-10 w-full max-w-[1240px] mx-auto px-5 lg:px-0">
            <div className="max-w-[460px]">
              <h1
                className="text-[34px] sm:text-[48px] md:text-[56px] leading-[1.1] font-bold mb-4"
                style={{ color: GREEN }}
              >
                {hero ? (
                  hero.title
                ) : (
                  <>
                    Pure Ayurveda
                    <br />
                    for a healthier you
                  </>
                )}
              </h1>
              {heroSub && (
                <p className="text-[14px] sm:text-[16px] text-[#555] mb-7">
                  {heroSub}
                </p>
              )}
              {heroBtnText &&
                (/^https?:\/\//.test(heroLink) ? (
                  <a href={heroLink}>{heroBtn}</a>
                ) : (
                  <Link to={heroLink}>{heroBtn}</Link>
                ))}
            </div>
          </div>
        )}
      </section>

      {/* ================= CATEGORY CARDS ================= */}
      <section
        className="w-full px-4 pt-8 pb-6 sm:pt-10"
        style={{ backgroundColor: CREAM }}
      >
        <div className="max-w-[1240px] mx-auto grid grid-cols-3 gap-3 sm:gap-6">
          {loadingCats &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg overflow-hidden border border-[#e4ead9] animate-pulse"
              >
                <div className="w-full aspect-[4/3] bg-[#eef2e6]" />
                <div className="h-4 bg-[#eef2e6] rounded m-4" />
              </div>
            ))}

          {!loadingCats &&
            categories.map((c) => (
              <Link
                key={c.id}
                to={c.to}
                className="group block bg-white rounded-lg overflow-hidden border border-[#e4ead9] hover:shadow-lg transition-shadow duration-300"
              >
                <div className="w-full aspect-[4/3] overflow-hidden bg-[#eef2e6]">
                  <img
                    src={c.img}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <p
                  className="text-center text-[12px] sm:text-[24px] font-[600] py-3 sm:py-4 px-1"
                  style={{ color: GREEN }}
                >
                  {c.title}
                </p>
              </Link>
            ))}
        </div>

        {!loadingCats && categories.length === 0 && (
          <p className="text-center text-[14px] text-[#777]">
            No categories found.
          </p>
        )}
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="w-full px-4 pb-8" style={{ backgroundColor: CREAM }}>
        <div className="max-w-[1240px] mx-auto grid grid-cols-2 lg:grid-cols-4 gap-y-4 gap-x-4 py-4 border-y border-[#dfe6d3]">
          {trustItems.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-center gap-2 sm:gap-3"
            >
              <t.icon
                className="text-[16px] sm:text-[20px]"
                style={{ color: GREEN }}
              />
              <span className="text-[12px] sm:text-[14px] text-[#333] font-[500]">
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ================= BEST SELLERS ================= */}
      <section className="w-full bg-white px-4 py-10 sm:py-14">
        <div className="max-w-[1240px] mx-auto">
          <div className="flex items-center justify-between mb-8 sm:mb-10">
            <h2
              className="text-[22px] sm:text-[30px] font-[600]"
              style={{ color: GREEN }}
            >
              Best Sellers
            </h2>
            <Link
              to="/shop"
              className="text-[12px] sm:text-[14px] font-[500] flex items-center gap-2 hover:underline"
              style={{ color: GREEN }}
            >
              View all <FaArrowRight className="text-[11px]" />
            </Link>
          </div>

          {error && (
            <p className="text-center text-[14px] text-red-600 mb-6">{error}</p>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-6">
            {loadingProducts && renderProductSkeletons()}
            {!loadingProducts && bestSellers.map(renderProductCard)}
          </div>

          {!loadingProducts && bestSellers.length === 0 && !error && (
            <p className="text-center text-[14px] text-[#777]">
              No best sellers yet.
            </p>
          )}
        </div>
      </section>

      {/* ================= RECENTLY ADDED ================= */}
      <section className="w-full bg-white px-4 pb-10 sm:pb-14">
        <div className="max-w-[1240px] mx-auto">
          <div className="flex items-center justify-between mb-8 sm:mb-10">
            <h2
              className="text-[22px] sm:text-[30px] font-[600]"
              style={{ color: GREEN }}
            >
              Recently Added
            </h2>
            <Link
              to="/shop"
              className="text-[12px] sm:text-[14px] font-[500] flex items-center gap-2 hover:underline"
              style={{ color: GREEN }}
            >
              View all <FaArrowRight className="text-[11px]" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-x-6">
            {loadingProducts && renderProductSkeletons()}
            {!loadingProducts && recentProducts.map(renderProductCard)}
          </div>

          {!loadingProducts && recentProducts.length === 0 && !error && (
            <p className="text-center text-[14px] text-[#777]">
              No new products yet.
            </p>
          )}
        </div>
      </section>

      {/* ================= WELLNESS. STYLE. YOU. BANNER ================= */}
      <section className="w-full px-4 pb-12 sm:pb-16 bg-white">
        <div
          className="relative max-w-[1240px] mx-auto rounded-xl overflow-hidden min-h-[140px] sm:min-h-[180px] flex items-center bg-cover bg-center"
          style={{ backgroundImage: `url(${Banner3})` }}
        >
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(90deg, ${GREEN} 0%, ${GREEN}e6 45%, ${GREEN}40 100%)`,
            }}
          />
          <div className="relative z-10 px-6 sm:px-12 py-6">
            <h2 className="text-white text-[24px] sm:text-[36px] font-[600] leading-tight mb-2">
              Wellness. Style. You.
            </h2>
            <p className="text-white/80 text-[12px] sm:text-[15px] mb-4">
              Explore our complete range
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-white text-[12px] sm:text-[14px] font-[600] px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
              style={{ color: GREEN }}
            >
              Shop now <FaArrowRight className="text-[11px]" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
